import React, { useEffect, useState } from 'react';
import { Card, CardContent, InputLabel, Typography, TextField, Select, MenuItem } from '@mui/material';
import CustomButton from '../../componentsII/Buttons';
import { showNotification } from '../../redux/slices/notificationSlice';
import api from '../../axios/api';
import { useSelector, useDispatch } from 'react-redux';

const AddReductionTarget = ({ availableYears, TargetYear, onClose, refetch }) => {
  const dispatch = useDispatch();
  const [selectedYear, setSelectedYear] = useState(TargetYear.id || '');
  const [reduction, setReduction] = useState(TargetYear.target_reduction_percentage || '');
  const user = useSelector((state) => state.user.user);

  const validateReduction = (value) => {
    const regex = /^(100(\.00?)?|[0-9]{1,2}(\.\d{0,2})?)$/; // Allows numbers up to 100 with up to two decimal places
    return regex.test(value);
  };

  const handleReductionChange = (e) => {
    let value = e.target.value;
    if (value === '') {
      setReduction('');
      return;
    }

    if (validateReduction(value) && parseFloat(value) > 0) {
      setReduction(value);
    }
  };

  const onSave = async () => {
    if (!selectedYear || !reduction) {
      dispatch(showNotification({ message: 'Please fill all required fields', type: 'error' }));
      return;
    }

    try {
      const response = await api.post(`/company/addUpdateReductionTarget`, {
        company_id: user.company.id,
        target_year_id: selectedYear,
        reduction_percentage: reduction,
      });

      if (response.data.status === '200') {
        dispatch(showNotification({ message: response.data.message, type: 'success' }));
        refetch();
      } else {
        dispatch(showNotification({ message: response.data.message, type: 'error' }));
      }
    } catch (e) {
      console.error(e);
      dispatch(showNotification({ message: 'An error occurred. Please try again.', type: 'error' }));
    }
  };

  return (
    <Card className="w-full max-w-lg p-5">
      <Typography variant="h5" fontWeight="bold" className="pt-5">
        Reduction Target Modelling
      </Typography>
      <CardContent className="pt-6">
        <div className="space-y-6">
          {/* Info Message */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">
              Companies usually set targets around IPCC scientific targets.{' '}
              <a href="#" className="text-purple-600 hover:text-purple-700">
                Learn more about target setting
              </a>
            </p>
          </div>

          {/* Target End Year */}
          <div className="space-y-2">
            <InputLabel htmlFor="targetYear" className="flex items-center gap-1">
              Target End Year <span className="text-red-500">*</span>
            </InputLabel>
            <Select
              id="targetYear"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="w-full"
              size="small"
            >
              {availableYears.map((year) => (
                <MenuItem key={year.id} value={year.id}>
                  {new Date(0, year.end_year_month - 1).toLocaleString('en-US', { month: 'short' })}{' '}
                  {year.end_year}
                </MenuItem>
              ))}
            </Select>
          </div>

          {/* Reduction Ambition */}
          <div className="space-y-2">
            <InputLabel htmlFor="reduction" className="flex items-center gap-1">
              Reduction Ambition <span className="text-red-500">*</span>
            </InputLabel>
            <div className="relative">
              <TextField
                id="reduction"
                type="number"
                className="w-full pr-8"
                placeholder="Enter percentage"
                size="small"
                value={reduction}
                onChange={handleReductionChange}
                inputProps={{
                  step: '0.01',
                  min: '0.01',
                  max: '100',
                }}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
            </div>
            <p className="text-sm text-gray-500">
              The percentage of emissions you plan to reduce by your target year.
            </p>
          </div>
        </div>
      </CardContent>
      <div className="w-full flex justify-end gap-5 items-center py-5">
        <CustomButton className="bg-gray-500 hover:bg-gray-700 text-white" click={onClose} text="Cancel" />
        <CustomButton className="bg-green-600 hover:bg-green-700 text-white" click={onSave} text="Submit" />
      </div>
    </Card>
  );
};

export default AddReductionTarget;
